import { useMemo, useState } from 'react'

import {
  Badge,
  Code,
  Group,
  NumberFormatter,
  Progress,
  Select,
  Stack,
  Text,
} from '@mantine/core'
import { IconReceipt2 } from '@tabler/icons-react'
import { Link } from '@tanstack/react-router'
import dayjs from 'dayjs'

import {
  type AdminRedemptionListItem,
  type AdminRedemptionStatus,
  useAdminRedemptions,
} from '@repo/common/queries/admin/redeem'

import { TowerCard } from '@/components/ui/TowerCard'

import styles from './RedemptionsTable.module.css'

interface Props {
  redeemableName: string
  redeemableId: string
}

const PHASES = [
  'awaiting',
  'receiving',
  'processing',
  'provisioning',
  'converting',
  'settling',
  'disbursing',
  'completed',
] as const

const REVERSAL_STATUSES = new Set([
  'reversing',
  'reversed',
  'deprovisioning',
  'unconverting',
])

const PHASE_INDEX: Record<string, number> = {}
PHASES.forEach((p, i) => {
  PHASE_INDEX[p] = i
})

function statusColor(status: AdminRedemptionStatus): string {
  if (status === 'completed') return 'green'
  if (status === 'failed') return 'red'
  if (REVERSAL_STATUSES.has(status)) return 'orange'
  if (status === 'awaiting') return 'gray'
  return 'blue'
}

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'awaiting', label: 'Awaiting' },
  { value: 'receiving', label: 'Receiving' },
  { value: 'processing', label: 'Processing' },
  { value: 'provisioning', label: 'Provisioning' },
  { value: 'converting', label: 'Converting' },
  { value: 'settling', label: 'Settling' },
  { value: 'disbursing', label: 'Disbursing' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'reversing', label: 'Reversing' },
  { value: 'reversed', label: 'Reversed' },
  { value: 'deprovisioning', label: 'Deprovisioning' },
  { value: 'unconverting', label: 'Unconverting' },
]

const ProgressBar: React.FC<{ status: AdminRedemptionStatus }> = ({
  status,
}) => {
  if (status === 'failed' || REVERSAL_STATUSES.has(status)) {
    const color = status === 'failed' ? 'red' : 'orange'
    return <Progress size="sm" radius="xl" value={100} color={color} />
  }

  const currentIdx = PHASE_INDEX[status] ?? 0
  const totalPhases = PHASES.length
  const percent = ((currentIdx + 1) / totalPhases) * 100
  const color = status === 'completed' ? 'green' : 'blue'

  return <Progress size="sm" radius="xl" value={percent} color={color} />
}

export const RedemptionsTable: React.FC<Props> = ({
  redeemableName,
  redeemableId,
}) => {
  const [statusFilter, setStatusFilter] = useState<string>('')

  const filters = useMemo(
    () => ({
      first: 50,
      redeemable_name: redeemableId,
      ...(statusFilter
        ? { status: statusFilter as AdminRedemptionStatus }
        : {}),
    }),
    [redeemableId, statusFilter],
  )

  const { data, isLoading } = useAdminRedemptions(filters)
  const redemptions = data?.redemptions.nodes ?? []

  return (
    <TowerCard
      title="Redemptions"
      description={`Redemptions for ${redeemableName}`}
      icon={<IconReceipt2 size={24} />}
      extraHeader={
        <Select
          size="xs"
          w={160}
          data={STATUS_OPTIONS}
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v ?? '')
          }}
          placeholder="Filter status"
          clearable
        />
      }
    >
      <Stack gap="xs">
        {isLoading ? (
          <Text ta="center" c="dimmed" py="lg">
            Loading redemptions...
          </Text>
        ) : redemptions.length === 0 ? (
          <Text ta="center" c="dimmed" py="lg">
            No redemptions found
          </Text>
        ) : (
          <>
            <div className={styles.table_wrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Status</th>
                    <th>Progress</th>
                    <th>Network</th>
                    <th className={styles.cell_right}>Deposit</th>
                    <th className={styles.cell_right}>Settlement</th>
                    <th className={styles.cell_right}>Fees</th>
                    <th>Created</th>
                    <th>Settled</th>
                  </tr>
                </thead>
                <tbody>
                  {redemptions.map((r: AdminRedemptionListItem) => (
                    <tr key={r.id}>
                      <td>
                        <Link
                          to="/redeem/$id/$redemptionId"
                          params={{
                            id: redeemableId,
                            redemptionId: r.id,
                          }}
                          className={styles.id_link}
                        >
                          {r.id.slice(0, 8)}...
                        </Link>
                      </td>
                      <td>
                        <Badge
                          size="xs"
                          variant="light"
                          color={statusColor(r.status)}
                        >
                          {r.status}
                        </Badge>
                      </td>
                      <td className={styles.progress_cell}>
                        <ProgressBar status={r.status} />
                      </td>
                      <td>
                        <Code style={{ fontSize: '0.75rem' }}>{r.network}</Code>
                      </td>
                      <td className={styles.cell_right}>
                        <NumberFormatter
                          value={r.deposit_total}
                          thousandSeparator
                        />
                      </td>
                      <td className={styles.cell_right}>
                        <NumberFormatter
                          value={r.settlement_total}
                          thousandSeparator
                        />
                      </td>
                      <td className={styles.cell_right}>
                        {r.fees_total != null ? (
                          <NumberFormatter
                            value={r.fees_total}
                            thousandSeparator
                          />
                        ) : (
                          <Text size="xs" c="dimmed">
                            —
                          </Text>
                        )}
                      </td>
                      <td>
                        <Text size="xs">
                          {r.deposit_confirmed_at
                            ? dayjs(r.deposit_confirmed_at).format(
                                'MMM D, h:mm A',
                              )
                            : dayjs(r.created_at).format('MMM D, h:mm A')}
                        </Text>
                      </td>
                      <td>
                        <Text size="xs">
                          {r.settled_at
                            ? dayjs(r.settled_at).format('MMM D, h:mm A')
                            : '—'}
                        </Text>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Showing {redemptions.length} of{' '}
                {data?.redemptions.total_count ?? 0} redemptions
              </Text>
            </Group>
          </>
        )}
      </Stack>
    </TowerCard>
  )
}
